<template>
  <form v-if="loaded" @submit.prevent="submit">
    <div class="row">
      <div v-if="fields.name_prefix" class="col-2 ps-0">
        <div class="form-group">
          <label>{{ $t('network.networkForm.prefix') }}</label>
          <input id="name_prefix" v-model="user_form.name_prefix" data-test="name_prefix" type="text" placeholder="Mr./Mrs./Ms./Dr." class="form-control"
          :class="!user_form.name_prefix && !inInviteMode ? 'border-2 border-info' : ''" />
        </div>
      </div>
      <div v-if="fields.first_name" class="col-4">
        <div class="form-group">
          <label>{{ $t('network.auth.firstName') }}</label><sup class="text-danger ms-1">*</sup>
          <input id="first_name" v-model="user_form.first_name" data-test="first_name" placeholder="John" class="form-control" trim required
          :class="!user_form.first_name && !inInviteMode ? 'border-2 border-info' : ''" />
          <div class="invalid-feedback"></div>
        </div>
      </div>
      <div v-if="fields.last_name" class="col-4">
        <div class="form-group">
          <label>{{ $t('network.auth.lastName') }}</label><sup class="text-danger ms-1">*</sup>
          <input id="last_name" v-model="user_form.last_name" data-test="last_name" placeholder="Doe" class="form-control" trim required
          :class="!user_form.last_name && !inInviteMode ? 'border-2 border-info' : ''" />
          <div class="invalid-feedback"></div>
        </div>
      </div>
      <div v-if="fields.name_suffix" class="col-2 pe-0">
        <div class="form-group">
          <label>{{ $t('network.auth.nameSuffix') }}</label>
          <input id="name_suffix" v-model="user_form.name_suffix" data-test="name_suffix" type="text" placeholder="MD/PhD/Jr/Sr." class="form-control"
          :class="!user_form.name_suffix && !inInviteMode ? 'border-2 border-info' : ''" />
        </div>
      </div>
    </div>
    <div class="row">
      <div v-if="fields.email" class="col my-3 p-0">
        <div class="form-group">
          <label>{{ $t('network.auth.email') }}</label><sup class="text-danger ms-1">*</sup>
          <input id="email" v-model="user_form.email" data-test="email" type="email" placeholder="email@gmail.com" class="form-control" trim required
          :class="!user_form.email && !inInviteMode ? 'border-2 border-info' : ''" />
        </div>
      </div>
    </div>
    <div class="row my-3">
      <div v-if="fields.phone_number" class="col-6 ps-0">
        <div class="form-group">
          <label>{{ $t('network.auth.phone') }}</label>
          <input id="phone_number" v-model="user_form.phone_number" data-test="phone_number" type="tel" placeholder="+1 555-111-1234" class="form-control" trim
          :class="!user_form.phone_number && !inInviteMode ? 'border-2 border-info' : ''" />
        </div>
      </div>
      <div v-if="fields.address" class="col-6 pe-0">
        <div class="form-group">
          <label>{{ $t('network.auth.address') }}</label>
          <input id="address" v-model="user_form.address" data-test="address" type="text" placeholder="4122 Buffalo Creek Road" class="form-control" trim
          :class="!user_form.address && !inInviteMode ? 'border-2 border-info' : ''" />
        </div>
      </div>
    </div>
    <div class="row my-3">
      <div v-if="fields.country" class="col-6 ps-0">
        <div class="form-group">
          <label>{{ $t('country-region') }}</label>
          <v-select
            id="country"
            v-model="user_form.country"
            data-test="country"
            :options="countries"
            label="name"
            :placeholder="$t('select-a-country-or-region')"
            :class="{'custom_border': !user_form.country && !inInviteMode}"
          >
          </v-select>
        </div>
      </div>
      <div v-if="fields.city" class="col-6 pe-0">
        <div class="form-group">
          <label>{{ $t('city-state') }}</label>
          <input v-if="user_form.country && user_form.country.name != 'UNITED STATES' && user_form.country != 'United States'" id="city" v-model="user_form.city" 
          data-test="city" type="text" placeholder="Nashville" class="form-control" trim :class="!user_form.city && !inInviteMode ? 'border-2 border-info' : ''" />
          <v-select
            v-else
            id="city"
            v-model="state"
            data-test="city"
            :options="states"
            label="name"
            :placeholder="$t('select-a-state')"
            :class="{'custom_border': !state && !inInviteMode}"
          >
          </v-select>
        </div>
      </div>
    </div>
    <div class="row my-3">
      <div class="form-group col-md-12 ps-0">
        <label>Type:<sup class="text-danger ms-1">*</sup></label><br>
        <label class="col-md-5">
          <input v-model="selectedRole" type="radio" name="role" value="is_hcp">&nbsp;
          Healthcare practitioner
        </label>
        <label class="col-md-3">
          <input v-model="selectedRole" type="radio" name="role" value="is_industry">&nbsp;
          Industry
        </label>
        <label class="col-md-4">
          <input v-model="selectedRole" type="radio" name="role" value="is_other">&nbsp;
          Other
        </label>
      </div>
    </div>
    <div v-if="profile">
      <hr style="background: repeating-linear-gradient(239deg, transparent, transparent 10px, black 10px, black 20px);margin: 20px 0px;">
      <div class="col-md-12">
        <div class="row">
          <div class="col-md-6 ps-0">
            <ContentLanguagePreference :updateMyPreferredLanguages="true" />
          </div>
          <div class="col-md-6">
            <div style="display: flex; justify-content: space-between;">
              <span>{{ $t('network.user.zoomAccount') }}</span>&nbsp;
              <ZoomSection v-if="this.$auth.auth && this.$auth.can('zoom.link-account')" :zoomUser="zoomUser ? zoomUser : ''" />
            </div>
            <div v-if="this.$auth.auth && this.$auth.can('zoom.link-account')" class="mt-1" style="font-size: smaller;">
              <span v-if="zoomUser && zoomUser.zoom_email">{{ $t('your-zoom-account-is-already-linked-with-resonance') }}</span>
              <span v-else>{{ $t('your-zoom-account-is-not-linked-with-resonance') }}</span>
            </div>
            <div v-else class="mt-1" style="font-size: smaller;">
              {{ $t('looking-to-integrate-meetings-within-networks') }}
              <router-link :to="'/unauthorized-zoom'">
                {{ $t('home.learnMore') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <button id="save" data-test="save" type="submit" class="btn btn-primary btn-block mt-3">
        <span>{{ $t('network.user.save') }}</span>
      </button>
    </div>
  </form>
  <div v-else class="row text-center mt-4">
    <lottie-player
      autoplay
      loop
	    speed="2.5"
      src="/img/animations/custom_loader.json"
      style="height: 130px;"
      >
    </lottie-player>
  </div>
</template>
<script>
import trim from 'lodash/trim';
import ContentLanguagePreference from '@/views/user/ContentLanguagePreference.vue';
import ZoomSection from '@/views/user/zoom/ZoomSection.vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

export default {
  components: {
    ContentLanguagePreference,
    ZoomSection,
    vSelect,
  },
  props: {
    form: {
      type: Object,
      default: null,
    },
    urlToken: {
      type: String,
      default: null,
    },
    inInviteMode: {
      type: Boolean,
      default: false,
    },
    profile: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Object,
      default: () => [{
        name_prefix: true,
        first_name: true,
        last_name: true,
        name_suffix: true,
        email: true,
        phone_number: true,
        address: true,
        city: true,
        country: true,
        is_hcp: true,
        is_industry: true,
        is_other: true,
      }],
    },
  },
  data() {
    return {
      loaded: false,
      invite: { shouldInvite: true },
      user_form: this.form,
      initialForm: '', // local version of the form (non reactive)
      user: this.$store.getters['user'],
      state: this.$store.getters['user'].state,
    };
  },
  computed: {
    formUpdated() {
      return JSON.stringify(this.form) != this.initialForm ? true : false;
    },
    zoomUser: function () {
      return this.user.zoom_user;
    },
    countries() {
      return this.$store.getters['countries'];
    },
    states() {
      return this.$store.getters['states'];
    },
    selectedRole: {
      get() {
        if (this.user_form.is_hcp) return 'is_hcp';
        if (this.user_form.is_industry) return 'is_industry';
        if (this.user_form.is_other) return 'is_other';
        return '';
      },
      set(value) {
        this.user_form.is_hcp = value === 'is_hcp';
        this.user_form.is_industry = value === 'is_industry';
        this.user_form.is_other = value === 'is_other';
      },
    },
  },
  watch: {
    formUpdated(value) {
      this.$emit('form-updated', value);
    },
  },
  created() {
    this.loadData();
  },
  mounted() {
    this.initialForm = JSON.stringify(this.form);
  },
  beforeDestroy() {
    this.state = '';
  },
  methods: {
    async loadData() {
      await this.$store.dispatch('loadCountries');
      await this.$store.dispatch('loadStates');
      this.loaded = true;
    },
    submit() {
      if (trim(this.user_form.first_name) == '' || trim(this.user_form.last_name) == '') {
        this.$swal.fire({
          icon: 'info',
          title: this.$t('first-name-and-last-name-are-required'),
        }).then(() => {
          return false;
        });
      }
      let form = this.user_form;
      if (this.state) {
        form.city = this.state;
      }
      this.$emit('user-form-submit', form);
    },
    checkIfUserExists() {
      if (this.user_form.email.length > 3) {
        this.loaded = false;
        this.axios
          .get('presenter/user?email=' + this.user_form.email)
          .then(resp => {
            if (resp.data.status) {
              this.user_form.first_name = trim(resp.data.data.first_name) != null ? resp.data.data.first_name : this.user_form.first_name;
              this.user_form.last_name = trim(resp.data.data.last_name) != '' ? resp.data.data.last_name : this.user_form.last_name;
              this.user_form.name_suffix = trim(resp.data.data.name_suffix) != '' ? resp.data.data.name_suffix : this.user_form.name_suffix;
              this.user_form.name_prefix = trim(resp.data.data.name_prefix) != '' ? resp.data.data.name_prefix : this.user_form.name_prefix;
              this.user_form.phone_number = trim(resp.data.data.phone_number) != '' ? resp.data.data.phone_number : this.user_form.phone_number;
              this.user_form.address = trim(resp.data.data.address) != '' ? resp.data.data.address : this.user_form.address;
              this.user_form.city = trim(resp.data.data.city) != '' ? resp.data.data.city : this.user_form.city;
              this.user_form.country = trim(resp.data.data.country) != '' ? resp.data.data.country : this.user_form.country;
              this.user_form.is_hcp = resp.data.data.is_hcp != '' ? resp.data.data.is_hcp : this.user_form.is_hcp;
              this.user_form.is_industry = resp.data.data.is_industry != '' ? resp.data.data.is_industry : this.user_form.is_industry;
              this.user_form.is_other = resp.data.data.is_other != '' ? resp.data.data.is_other : this.user_form.is_other;
              this.loaded = true;
            }
          })
          .catch(err => {
            console.log('err=', err);
            // this.$router.push('/networks'); // back to the homepage
          });
      }
    },
  },
};
</script>
<style>
.custom_border {
  border: 2px solid rgba(33, 150, 243, 0.4) !important;
}
</style>